import { Link } from "react-router-dom";
import { Clock, ArrowUpRight } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { ARTICLES, ARTICLE_IMAGES, IMAGES } from "@/data/site";
import Seo from "@/components/Seo";

const Insights = () => {
  const [featured, ...rest] = ARTICLES;
  return (
    <div data-testid="insights-page">
      <Seo
        title="Planning Insights"
        description="Editorial insights on NSW planning — what changing rules, policies and precedents mean for your property, purchase or project."
      />
      <section className="bg-sand-light py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Eyebrow>Resources &amp; Insights</Eyebrow>
          <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-teal">
            Practical planning intelligence
          </h1>
          <p className="mt-5 max-w-2xl text-charcoal/70 leading-relaxed">
            Regular explainers on the NSW planning questions real property owners are asking —
            from DA timelines to backyard granny flats.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <Link
              to={`/hub/${featured.slug}`}
              data-testid="insights-featured-article"
              className="group grid lg:grid-cols-2 bg-white border border-cloud hover:border-teal transition-colors overflow-hidden"
            >
              <div className="p-10 lg:p-14 flex flex-col justify-center">
                <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-teal/60">
                  Featured · {featured.category}
                </span>
                <h2 className="mt-4 font-serif text-2xl sm:text-3xl text-teal tracking-tight leading-snug">
                  {featured.title}
                </h2>
                <p className="mt-4 text-charcoal/70 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-teal font-medium border-b-2 border-sand pb-1 w-fit group-hover:border-teal transition-colors">
                  Read the guide <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
              <div className="bg-teal text-ivory p-10 lg:p-14 flex flex-col justify-center">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-sand">
                  The NSW Planning Memo
                </p>
                <p className="mt-4 font-serif text-xl sm:text-2xl leading-relaxed">
                  "Most DA delays are self-inflicted. A complete lodgement beats a fast one,
                  every single time."
                </p>
                <p className="mt-4 text-ivory/60 text-sm">
                  — from this month's member briefing
                </p>
              </div>
            </Link>
          </FadeIn>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.slice(0, 6).map((a, i) => (
              <FadeIn key={a.slug} delay={(i % 3) * 0.06} className="h-full">
                <Link
                  to={`/hub/${a.slug}`}
                  className="group flex flex-col h-full bg-white border border-cloud hover:border-teal hover:-translate-y-1 hover:shadow-lg transition-all duration-300 overflow-hidden"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-sand-light">
                    <img
                      src={IMAGES[ARTICLE_IMAGES[a.slug]]}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-charcoal/50">
                    {a.category}
                  </span>
                  <h3 className="mt-3 font-serif text-lg text-teal leading-snug flex-1">
                    {a.title}
                  </h3>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-xs text-charcoal/50">
                    <Clock className="w-3.5 h-3.5" /> {a.minutes} min read
                  </span>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Insights;
